import { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../api/client';
import { Loading, ApiError } from '../components/ui/Loading';
import { CartableModal } from '../components/ui/CartableModal';
import { formatPrice, formatDateTime } from '../utils/helpers';

const AUTO_REFRESH_INTERVAL = 60000; // 60 secondes

// ─── Bloc résumé (une des 3 colonnes du header) ───────────────────────────────
const SummaryBlock = ({ icon, title, subtitle, value, color, subValue, subLabel }) => (
  <div className="card" style={{
    flex: 1, minWidth: 200,
    borderTop: `3px solid ${color}`,
    display: 'flex', flexDirection: 'column', gap: 8,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: 22 }}>{icon}</span>
      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {title}
        </div>
        {subtitle && <div style={{ fontSize: 11, color: 'var(--text3)' }}>{subtitle}</div>}
      </div>
    </div>
    <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 28, color }}>{value}</div>
    {subValue != null && (
      <div style={{ fontSize: 12, color: 'var(--text2)' }}>
        <span style={{ color: 'var(--text3)' }}>{subLabel} : </span>
        <span style={{ fontWeight: 600 }}>{subValue}</span>
      </div>
    )}
  </div>
);

// ─── Section dépliable avec tableau détail ────────────────────────────────────
const DetailSection = ({ title, icon, color, rows, columns, onOpenCartable }) => {
  const [open, setOpen] = useState(false);

  if (!rows?.length) return null;

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px', background: 'none', border: 'none', cursor: 'pointer',
          borderBottom: open ? '1px solid var(--border)' : 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 18 }}>{icon}</span>
          <span style={{ fontWeight: 700, fontSize: 14 }}>{title}</span>
          <span style={{
            background: color, color: '#fff', borderRadius: 20,
            padding: '2px 10px', fontSize: 11, fontWeight: 700,
          }}>
            {rows.length}
          </span>
        </div>
        <span style={{ color: 'var(--text3)', fontSize: 18 }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>{columns.map((c, i) => <th key={i}>{c.label}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={i}
                  style={{ cursor: onOpenCartable ? 'pointer' : 'default' }}
                  onClick={() => onOpenCartable && onOpenCartable(row)}
                  title={onOpenCartable ? 'Cliquer pour voir le cartable' : ''}
                >
                  {columns.map((c, j) => (
                    <td key={j} style={c.style}>
                      {c.render ? c.render(row) : row[c.key] ?? '—'}
                    </td>
                  ))}
                  {onOpenCartable && (
                    <td onClick={e => { e.stopPropagation(); onOpenCartable(row); }}>
                      <button className="btn btn-ghost btn-sm">📒</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Page principale ──────────────────────────────────────────────────────────
export const FinanceOverview = () => {
  const [data,         setData]         = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [autoRefresh,  setAutoRefresh]  = useState(true);
  const [lastRefresh,  setLastRefresh]  = useState(null);
  const [cartable,     setCartable]     = useState(null); // { id, name }

  const fetchOverview = useCallback(async () => {
    try {
      setError(null);
      const res = await adminApi.getWalletOverview();
      setData(res.data);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(fetchOverview, AUTO_REFRESH_INTERVAL);
    return () => clearInterval(id);
  }, [autoRefresh, fetchOverview]);

  if (loading) return <Loading text="Chargement de la supervision financière..." />;
  if (error)   return <ApiError message={error} onRetry={fetchOverview} />;
  if (!data)   return null;

  const { livreurs, laveries, enterprises, lavex_commission_month_to_date } = data;

  // ── Recalcul net_balance depuis le détail (v3.4 : nom officiel = net_owed_to_lavex) ─
  // Si net_balance backend = 0 (bug connu), on somme le détail
  const livreurNetBalance = (() => {
    if (livreurs?.detail?.length) {
      return livreurs.detail.reduce((acc, r) => acc + (r.net_owed_to_lavex ?? 0), 0);
    }
    return livreurs?.net_balance ?? 0;
  })();

  // Gain net LAVEX = commissions laveries + montants dus par livreurs (positifs) + entreprises
  // Partie déjà encaissée : commission mois en cours
  // Partie à encaisser : livreurs positifs + entreprises
  const livreursDuPositif  = livreurNetBalance > 0 ? livreurNetBalance : 0;
  const lavexGainTotal     = (lavex_commission_month_to_date ?? 0)
                             + livreursDuPositif
                             + (enterprises?.total_owed_to_lavex ?? 0);

  return (
    <div>
      {/* En-tête */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 24, marginBottom: 4 }}>
            💰 Supervision Financière
          </div>
          <div style={{ fontSize: 13, color: 'var(--text2)' }}>
            État des flux en temps réel — mis à jour à chaque commande livrée
          </div>
          <div className="inline-alert inline-alert-blue" style={{ marginTop: 10, fontSize: 12 }}>
            <span>ℹ️</span>
            <div>
              Cette vue affiche <strong>uniquement ce qui est encore en circulation cette semaine/ce mois</strong>.
              Une fois la période clôturée (lundi pour livreurs, 1er du mois pour laveries/entreprises),
              les montants sortent d'ici et apparaissent dans <strong>Règlements &amp; Paiements</strong> —
              c'est voulu, pas un bug de synchronisation.
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {lastRefresh && (
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>
              ↻ {lastRefresh.toLocaleTimeString('fr-FR')}
            </span>
          )}
          <button
            className={`btn btn-sm ${autoRefresh ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setAutoRefresh(v => !v)}
          >
            {autoRefresh ? '⏸ Auto (60s)' : '▶ Auto'}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={fetchOverview}>
            ↺ Actualiser
          </button>
        </div>
      </div>

      {/* 3 blocs résumé */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <SummaryBlock
          icon="🛵"
          title="Livreurs nous doivent"
          subtitle="Espèces à reverser (net commission)"
          value={formatPrice(livreurNetBalance > 0 ? livreurNetBalance : 0)}
          color={livreurNetBalance < 0 ? 'var(--blue)' : 'var(--orange)'}
          subLabel={livreurNetBalance < 0 ? 'LAVEX doit aux livreurs' : 'Encaissé total'}
          subValue={livreurNetBalance < 0
            ? formatPrice(Math.abs(livreurNetBalance))
            : formatPrice(livreurs?.total_cash_to_collect ?? 0)}
        />
        <SummaryBlock
          icon="🏪"
          title="Profit net laveries"
          subtitle="Nous devons verser à fin de mois"
          value={formatPrice(laveries?.net_profit ?? 0)}
          color="var(--blue)"
          subLabel="Revenus services"
          subValue={formatPrice(laveries?.total_service_revenue ?? 0)}
        />
        <SummaryBlock
          icon="🏢"
          title="Entreprises nous doivent"
          subtitle="Facturation mensuelle en cours"
          value={formatPrice(enterprises?.total_owed_to_lavex ?? 0)}
          color="var(--purple)"
        />
      </div>

      {/* ── Bloc Gain Net LAVEX ── */}
      <div className="card" style={{
        marginBottom: 24,
        border: '2px solid var(--accent)',
        background: 'linear-gradient(135deg, rgba(91,106,240,0.04), rgba(91,106,240,0.01))',
      }}>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {/* Gain total */}
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>
              📊 Gain Net LAVEX — Mois en cours
            </div>
            <div style={{ fontFamily: 'Syne', fontWeight: 900, fontSize: 32, color: 'var(--accent)' }}>
              {formatPrice(lavexGainTotal)}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>
              Commissions + créances livreurs + créances entreprises
            </div>
          </div>

          <div style={{ width: 1, height: 60, background: 'var(--border)', flexShrink: 0 }} />

          {/* Décomposition */}
          <div style={{ flex: 2, minWidth: 260, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {[
              {
                label:    '✅ Commissions laveries',
                sublabel: 'Déjà acquises ce mois',
                value:    lavex_commission_month_to_date ?? 0,
                color:    'var(--green)',
                tag:      'Encaissé',
                tagCls:   'badge-green',
              },
              {
                label:    '🛵 Livreurs (espèces)',
                sublabel: livreurNetBalance < 0 ? 'LAVEX doit verser' : 'À collecter des livreurs',
                value:    livreurNetBalance,
                color:    livreurNetBalance < 0 ? 'var(--blue)' : 'var(--orange)',
                tag:      livreurNetBalance < 0 ? 'À verser' : 'À collecter',
                tagCls:   livreurNetBalance < 0 ? 'badge-blue' : 'badge-orange',
              },
              {
                label:    '🏢 Entreprises',
                sublabel: 'Factures en attente de paiement',
                value:    enterprises?.total_owed_to_lavex ?? 0,
                color:    'var(--purple)',
                tag:      'À facturer',
                tagCls:   'badge-purple',
              },
            ].map((item, i) => (
              <div key={i} style={{
                flex: 1, minWidth: 140,
                background: 'var(--bg3)', borderRadius: 10, padding: '12px 14px',
                borderLeft: `3px solid ${item.color}`,
              }}>
                <div style={{ fontSize: 11, color: 'var(--text2)', fontWeight: 600, marginBottom: 4 }}>{item.label}</div>
                <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 18, color: item.color, marginBottom: 4 }}>
                  {item.value < 0 ? '-' : ''}{formatPrice(Math.abs(item.value))}
                </div>
                <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 6 }}>{item.sublabel}</div>
                <span className={`badge ${item.tagCls}`} style={{ fontSize: 10 }}>{item.tag}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tableaux détail dépliables */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

        {/* Livreurs */}
        <DetailSection
          title="Détail par livreur"
          icon="🛵"
          color="var(--orange)"
          rows={livreurs?.detail}
          onOpenCartable={row => setCartable({ id: row.user_id, name: row.name })}
          columns={[
            { label: 'Livreur', key: 'name', render: r => (
              <div>
                <div className="td-main">{r.name}</div>
                <div className="td-sub">📞 {r.phone || '—'}</div>
              </div>
            )},
            { label: 'Encaissé',      key: 'collected',  render: r => <span style={{ color: 'var(--text2)' }}>{formatPrice(r.collected ?? 0)}</span> },
            { label: 'Commission',    key: 'commission', render: r => <span style={{ color: 'var(--green)', fontWeight: 600 }}>+{formatPrice(r.commission ?? 0)}</span> },
            { label: 'Net dû à LAVEX', key: 'net_owed_to_lavex', render: r => {
              const v = r.net_owed_to_lavex ?? 0;
              return (
                <span style={{ fontWeight: 700, color: v < 0 ? 'var(--blue)' : v === 0 ? 'var(--text3)' : 'var(--red)' }}>
                  {v < 0
                    ? `LAVEX doit ${formatPrice(Math.abs(v))}`
                    : v === 0 ? '0.00 MAD'
                    : formatPrice(v)}
                </span>
              );
            }},
          ]}
        />

        {/* Laveries */}
        <DetailSection
          title="Détail par laverie"
          icon="🏪"
          color="var(--blue)"
          rows={laveries?.detail}
          onOpenCartable={row => setCartable({ id: row.user_id ?? row.laverie_id, name: row.name })}
          columns={[
            { label: 'Laverie',    key: 'name',       render: r => <div className="td-main">{r.name}</div> },
            { label: 'Revenus',    key: 'revenue',    render: r => <span style={{ color: 'var(--text2)' }}>{formatPrice(r.revenue)}</span> },
            { label: 'Commission', key: 'commission', render: r => <span style={{ color: 'var(--red)' }}>-{formatPrice(r.commission)}</span> },
            { label: 'Profit net', key: 'net_profit', render: r => (
              <span style={{ fontWeight: 700, color: 'var(--green)' }}>{formatPrice(r.net_profit)}</span>
            )},
          ]}
        />

        {/* Entreprises */}
        <DetailSection
          title="Détail par entreprise"
          icon="🏢"
          color="var(--purple)"
          rows={enterprises?.detail}
          onOpenCartable={row => setCartable({ id: row.user_id, name: row.name })}
          columns={[
            { label: 'Entreprise', key: 'name',        render: r => <div className="td-main">{r.name}</div> },
            { label: 'Montant dû', key: 'total_owed',  render: r => (
              <span style={{ fontWeight: 700, color: 'var(--accent)' }}>{formatPrice(r.total_owed)}</span>
            )},
          ]}
        />
      </div>

      {/* CartableModal */}
      <CartableModal
        open={!!cartable}
        onClose={() => setCartable(null)}
        userId={cartable?.id}
        userName={cartable?.name}
      />
    </div>
  );
};